/**
 * Results — what the table thought, once the gems have landed.
 *
 * Three rules from the plan shape this screen, and all three are the reason it
 * is not a scorecard:
 *
 *  1. **The two numbers are about the night, not about the game.** One is the
 *     headline, typed in directly by every player; the other is how good the
 *     evening was, which is the mean of that seat's topics unless they set it
 *     themselves. Neither is a verdict on the box, and the ranking below them is
 *     still by *spread* rather than by score — a fact about the group, not a
 *     quality judgement about the game. Nothing else on this screen rolls
 *     aspects up, and nothing rolls nights up.
 *  2. **A distribution is never a bare mean.** Every aggregate is drawn as one
 *     dot per rater in that rater's own colour, carries its own `n`, and names
 *     its modal stop in words. The faint mean line is context behind the dots,
 *     and it is drawn only where a mean is meaningful — the headline and
 *     evaluative aspects. A descriptive axis never gets one: the mean of a
 *     Luck↔Strategy track describes no game anybody played.
 *  3. **A split is the finding.** The widest disagreement leads the screen. It
 *     is the sentence the app exists to produce, so it is not buried under a
 *     tidy summary of the things everyone already agreed about.
 *
 * Reveal state is read, never inferred. An aspect only reveals once every
 * present participant has committed a value for it, and aspects are skippable —
 * so an aspect somebody skipped stays blind forever, and aggregating it would
 * produce a "distribution" made of this device's own single row wearing the
 * group's clothes. Those are listed as still blind instead.
 */

import { Fragment, useCallback, useMemo, useState } from "react";
import type { CSSProperties, ReactNode } from "react";

import { DeleteNight } from "../../components/DeleteNight.tsx";
import { navigate } from "../../main.tsx";
import type { ScreenProps } from "../../main.tsx";
import { formatStars, starLabel } from "../../../shared/stars.ts";
import { BoxArt } from "../catalog/components.tsx";
import { ApiError, finishSession, offlineLine, readGrant, writePathFor } from "./api.ts";
import { channelNotice, useSessionLive } from "./useSession.ts";
import {
  HEADLINE_STOPS,
  OVERALL_STOPS,
  aspectProfile,
  aspectSlices,
  buildSteps,
  cellsOf,
  disagreement,
  followUpsByAspect,
  headlineDistribution,
  missingHeadline,
  overallCells,
  radarHasShapes,
  ratingCells,
  seatColour,
  seatRadar,
  topicCellsOf,
  visibleRatingCells,
} from "./state.ts";
import { RADAR_MIN_AXES, Radar } from "../../components/Radar.tsx";
import type { AspectView, Cell, SessionState, Step } from "./state.ts";
import { SPLIT_THRESHOLD } from "../../../shared/aggregate.ts";
import type { Disagreement, Distribution } from "../../../shared/aggregate.ts";
import type { SessionParticipant, Stop } from "../../../shared/types.ts";

/** The server's answer when the table is not finished. Not an error shape. */
interface MissingSeat {
  participant_id: string;
  name: string;
}

/** Raw server strings are for logs. The table gets a sentence. */
function completionError(err: unknown): string {
  if (!(err instanceof ApiError)) return "Could not reach the server. Try again.";
  if (err.status === 401 || err.status === 403) return "Only the host can finish the session.";
  if (err.status === 404) return "This session is no longer on the server.";
  return `Could not finish the session (${err.message}).`;
}

// ---------------------------------------------------------------------------
// Copy
// ---------------------------------------------------------------------------

/**
 * The headline half of the group verdict.
 *
 * Median rather than mean: one person who hated it should not be able to drag
 * the sentence, and one person who loved it should not be able to buy it.
 *
 * Before the reveal the only visible row is the actor's own, so ANY sentence
 * built from the numbers would print this device's own rating to the next
 * person holding the phone. Blind means blind in the prose too.
 */
function headlineClause(d: Distribution, revealed: boolean): string {
  if (!revealed) return "The one number is still held back until everyone has answered";
  if (d.n === 0) return "Nobody has said yet whether they would play this again";
  if (d.n === 1) {
    const only = starLabel(HEADLINE_STOPS, d.min) ?? String(d.min);
    return `Only one answer so far — ${only.toLowerCase()}`;
  }
  if (d.split) {
    return `You did not play the same game — playing it again ran from ${formatStars(d.min)} to ${formatStars(d.max)} stars`;
  }
  const m = d.median ?? 0;
  if (m >= 9) return "The table wants this back on the table tonight";
  if (m >= 7.5) return "The table would suggest this one again";
  if (m >= 6) return "The table is happy to play this again";
  if (m >= 4) return "The table would play this again if somebody asked";
  if (m >= 2.5) return "The table is not keen on playing this again";
  return "The table does not want this one again";
}

/** One line. The headline, and the thing you argued about. Nothing else. */
function verdictLine(
  headline: Distribution,
  revealed: boolean,
  top: Disagreement | null,
): string {
  const head = headlineClause(headline, revealed);
  if (!top) return `${head}.`;
  const d = top.profile.distribution;
  if (d.split) return `${head}, but you were split on ${top.name}.`;
  // "everything you rated" would overclaim: aspects still blind are not in the
  // ranking at all, so the claim is scoped to what actually landed.
  if (d.spread === 0) return `${head}, and nothing that landed split the table.`;
  return `${head}, and ${top.name} is the closest thing you had to an argument.`;
}

/**
 * The badge over the transport notice.
 *
 * It said "Live" on every session this screen has ever drawn, including ones
 * that ended weeks ago. A finished night has no live channel; the only reason
 * it prints a notice at all is that the copy on screen came off the cache, and
 * the ochre "wait" tone would claim something is still pending on it.
 */
export function channelBadge(finished: boolean): { label: string; tone?: string } {
  return finished ? { label: "Offline" } : { label: "Live", tone: "wait" };
}

function playedOn(at: number): string {
  const d = new Date(at);
  const pad = (n: number): string => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

// ---------------------------------------------------------------------------
// The rail
// ---------------------------------------------------------------------------

/**
 * Vertical fan for raters who landed on the same stop.
 *
 * Four people agreeing must read as four people, not as one dot. The rail's
 * padding box is 24px (26 minus its two 1px borders) and a dot is 14px, so ±5px
 * is the whole budget — a wider fan spills over the rounded edge.
 */
function dotOffsets(count: number): number[] {
  if (count <= 1) return [0];
  const step = Math.min(5, 10 / (count - 1));
  return Array.from({ length: count }, (_, i) => (i - (count - 1) / 2) * step);
}

interface RailProps {
  stops: readonly Stop[];
  dist: Distribution;
  /** Visible, rated, non-N/A cells, in seat order. */
  dots: Cell[];
  showMean: boolean;
}

function Rail(props: RailProps): ReactNode {
  const { stops, dist, dots, showMean } = props;

  // The scale is the rubric's, not the data's: a track where everyone agreed
  // must not stretch that one stop across the full width.
  const values = stops.map((s) => s.value);
  const lo = values.length > 0 ? Math.min(...values) : dist.min;
  const hi = values.length > 0 ? Math.max(...values) : dist.max;
  const at = (v: number): number => (hi === lo ? 0.5 : (v - lo) / (hi - lo));

  /**
   * One coordinate system for all three marks, insets included.
   *
   * Notch, mean line and dot each carry their own centring margin in CSS, so
   * they agree on a centre only if they agree on the track. Inset by half a dot
   * at both ends: the widest mark is what decides where the track can start,
   * and a gem must sit IN its notch, not six pixels beside it.
   */
  const mark = (v: number): string => `calc(7px + (100% - 14px) * ${at(v)})`;

  const stacks = new Map<number, Cell[]>();
  for (const cell of dots) {
    if (cell.value === null) continue;
    const bucket = stacks.get(cell.value);
    if (bucket) bucket.push(cell);
    else stacks.set(cell.value, [cell]);
  }

  return (
    // Hidden from assistive tech on purpose: the attributed rows below say the
    // same thing in words, and announcing both reads the table out twice.
    <div className="pb-dist__rail" aria-hidden="true">
      {stops.map((stop) => (
        <span
          key={`notch-${stop.value}`}
          className="pb-dist__notch"
          style={{ left: mark(stop.value) }}
        />
      ))}
      {showMean && dist.mean !== null && (
        <span className="pb-dist__mean" style={{ left: mark(dist.mean) }} />
      )}
      {/* Flattened, so the participant id really is the key: a Map keyed by
          value reorders as people change their minds, and nested arrays would
          make React fall back to the stack's position. */}
      {[...stacks.entries()].flatMap(([value, group]) => {
        const offsets = dotOffsets(group.length);
        return group.map((cell, i) => {
          const style: CSSProperties = {
            left: mark(value),
            transform: `translateY(calc(-50% + ${offsets[i] ?? 0}px))`,
            background: seatColour(cell.participant),
            // A dark profile colour on the dark theme's sunk rail is otherwise
            // a black dot with a black border. Position carries the meaning,
            // but the dot still has to be findable.
            outline: "1px solid var(--pb-rule-strong)",
          };
          return (
            <span
              key={cell.participant.id}
              className="pb-dist__dot"
              style={style}
              title={`${cell.participant.name_snapshot}: ${starLabel(stops, value) ?? formatStars(value)}`}
            />
          );
        });
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// One aggregate
// ---------------------------------------------------------------------------

interface BlockProps {
  title: string;
  /** One plain sentence about what the shape of this means. */
  lead?: string;
  stops: readonly Stop[];
  dist: Distribution;
  modalLabel: string | null;
  na: number;
  /** Every seat, answered or not — attribution survives the reveal. */
  cells: Cell[];
  /** Seats at the table, so `n` can be read as "4 of 5". */
  total: number;
  /** Descriptive axes never get one. See the header note. */
  showMean: boolean;
  /** The star figure is itself the datum; a stop label alone loses it. */
  numeric?: boolean;
  edge: string;
}

function Block(props: BlockProps): ReactNode {
  const { title, lead, stops, dist, modalLabel, na, cells, total, showMean, numeric, edge } = props;

  // Every figure on this screen is stars, never the stored 1–10. The stored
  // number counts half-stars, so printing it raw doubles what the rater set —
  // a table that all agreed on three stars read back as "mean 6.0".
  const meanText = showMean && dist.mean !== null ? ` · mean ${formatStars(dist.mean)}` : "";
  const naText = na > 0 ? ` · ${na} n/a` : "";

  return (
    <section className="pb-tile pb-tile__stack" data-edge={edge}>
      <div className="pb-screen__head">
        <h2 className="pb-tile__title">{title}</h2>
        {dist.split && <span className="pb-split">Split</span>}
      </div>

      {lead && <p className="pb-hint">{lead}</p>}

      <div className="pb-dist">
        <Rail stops={stops} dist={dist} dots={cells.filter(isDot)} showMean={showMean} />
        <div className="pb-dist__legend">
          <span className="pb-dist__modal">{modalLabel ?? "No answers"}</span>
          <span className="pb-dist__n">
            n = {dist.n} of {total}
            {naText} · spread {formatStars(dist.spread)}
            {meanText}
          </span>
        </div>
      </div>

      <div className="pb-roster">
        {cells.map((cell) => (
          <Fragment key={cell.participant.id}>
            <div className="pb-cell">
              <span className="pb-seat">
                <span
                  className="pb-swatch"
                  style={{
                    background: seatColour(cell.participant),
                    outline: "1px solid var(--pb-rule-strong)",
                  }}
                />
                <span className="pb-seat__name">{cell.participant.name_snapshot}</span>
              </span>
              <span className="pb-cell__label" data-na={isDot(cell) ? "false" : "true"}>
                {cellText(cell, stops, numeric === true)}
              </span>
            </div>
            {cell.note && <p className="pb-cell__note">&ldquo;{cell.note}&rdquo;</p>}
          </Fragment>
        ))}
      </div>
    </section>
  );
}

/** A real, revealed value — as opposed to a skip, an N/A or a blind row. */
function isDot(cell: Cell): boolean {
  return cell.visible && !cell.na && cell.value !== null;
}

function cellText(cell: Cell, stops: readonly Stop[], numeric: boolean): string {
  if (!cell.committed) return "no answer";
  if (!cell.visible) return "hidden";
  // The note is read before `na`: a skip and an answer given in words are both
  // stored as n/a, and only the note tells them apart.
  if (cell.value === null) {
    if (cell.note) return "said it in words";
    return cell.na ? "doesn't apply" : "skipped";
  }
  const stars = formatStars(cell.value);
  const label = starLabel(stops, cell.value);
  if (!label) return stars;
  return numeric ? `${stars} · ${label}` : label;
}

// ---------------------------------------------------------------------------
// Screen
// ---------------------------------------------------------------------------

export function Results(props: ScreenProps): ReactNode {
  const id = props.params["id"] ?? "";
  /**
   * Read, and never thrown away from here. The one screen that must not.
   *
   * The rating screen checks a failed read against the server and the join screen
   * drops a grant the server has stopped honouring, and the obvious next step is a
   * third copy of that here, because this screen fails the same way when a token
   * dies. It must not have one. A guest's grant deliberately outlives the night by
   * a day so the phone that helped fill the board can still read it (`9c13852`),
   * and this screen is that board — the artefact the evening exists to produce.
   * The refusal that would fire the clear cannot be told apart from one arriving a
   * moment before the same phone was going to be let in, and getting it wrong here
   * costs the guest the one thing the previous fix was written to give them.
   *
   * Nor would clearing help anybody: with no grant this screen sends no token, so
   * the read goes out as the owner's, comes back `401`, and the app puts the
   * sign-in page in front of somebody who has no account. The honest refusal it
   * replaces at least says what happened and what to do about it.
   */
  const grant = useMemo(() => readGrant(id), [id]);

  // No `as`: results is the whole table's screen, so nothing is redacted down
  // to one seat. A guest device still identifies itself by its grant.
  const { state, error, loading, channel, cancelled, refresh } = useSessionLive(id, {
    token: grant?.token ?? null,
    enabled: id !== "",
  });

  const [busy, setBusy] = useState(false);
  const [serverMissing, setServerMissing] = useState<MissingSeat[] | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [justFinished, setJustFinished] = useState(false);

  const finish = useCallback(async () => {
    setBusy(true);
    setFailure(null);
    try {
      // Drained first, and not completed at all if anything is still queued: the
      // server refuses a rating whose session is no longer DRAFT, so a rating
      // sitting in the outbox at this moment is a rating lost for good.
      const outcome = await finishSession(id);
      if (outcome.kind === "unsent") {
        setFailure(
          "Some ratings on this device haven't reached the table yet. Get back on the network and try again — nothing is lost.",
        );
        return;
      }
      const result = outcome.result;
      if (result.ok) {
        setServerMissing(null);
        setJustFinished(true);
        void refresh();
        return;
      }
      // 200 with `ok: false`. "We are still waiting on Lea" is a screen, not a
      // failure, so it must never be reached by catching a non-2xx.
      setServerMissing(result.missing);
    } catch (err) {
      setFailure(completionError(err));
    } finally {
      setBusy(false);
    }
  }, [id, refresh]);

  if (!id) {
    return (
      <div className="pb-empty">
        <div className="pb-label">No session</div>
        <p>That link is missing a session.</p>
      </div>
    );
  }

  // Above every guard that reads `state`, because `state` still holds the board
  // as it was a moment ago and this screen would go on showing it — a finished
  // night reads as an artefact, so nothing about a frozen copy of one looks
  // wrong. The board is not stale here, it is gone.
  if (cancelled) {
    return (
      <div className="pb-empty">
        <div className="pb-label">These results are gone</div>
        <p>
          Whoever set this game up deleted the night. Every rating on it went with it — nothing
          was kept, and this board is not coming back.
        </p>
        {grant === null ? (
          <button
            type="button"
            className="pb-btn"
            data-variant="primary"
            onClick={() => navigate("/", { replace: true })}
          >
            Back to the shelf
          </button>
        ) : (
          // A guest phone has no account, so every route in this app would put a
          // sign-in page in front of somebody who wants none.
          <p>Nothing more to do on this phone — you can hand it back.</p>
        )}
      </div>
    );
  }

  if (loading && !state) {
    return (
      <div className="pb-empty">
        <div className="pb-label">Results</div>
        <p>Reading the table&hellip;</p>
      </div>
    );
  }

  if (!state) {
    return (
      <div className="pb-screen">
        <div className="pb-notice" data-tone="alert">
          <span className="pb-notice__strong">Error</span>
          <span>{error ?? "This session could not be loaded."}</span>
        </div>
        <div className="pb-actions">
          <button type="button" className="pb-btn" onClick={() => void refresh()}>
            Try again
          </button>
        </div>
      </div>
    );
  }

  // The same reading of "over" the view below uses, because the transport line
  // is chosen up here and the badge over it down there.
  const finished = state.session.status === "COMPLETED" || justFinished;

  return (
    <ResultsView
      state={state}
      error={error}
      // Offline copy has to match the write path this device really uses: a
      // guest, and the owner on a QR night, hold nothing locally. And on a
      // night that is already over there is no write path at all — nothing is
      // being held for anybody, so the write-path line would invent a queue.
      channel={
        channel === "offline" && !finished
          ? offlineLine(writePathFor(state.session.capture_mode, grant ? "guest" : "owner"))
          : channelNotice(channel, finished)
      }
      busy={busy}
      failure={failure}
      justFinished={justFinished}
      serverMissing={serverMissing}
      // A guest device holds a seat grant, not the owner's cookie, and the
      // complete endpoint refuses grants outright. Offering it the button would
      // only ever produce an authorisation error.
      isGuest={grant !== null}
      onFinish={() => void finish()}
      // Home, not the game's page, and not this one. The night this screen is
      // about no longer exists, so the only route that is certainly still there
      // is the shelf.
      onDeleted={() => navigate("/", { replace: true })}
    />
  );
}

interface ViewProps {
  state: SessionState;
  error: string | null;
  channel: string | null;
  busy: boolean;
  failure: string | null;
  justFinished: boolean;
  serverMissing: MissingSeat[] | null;
  isGuest: boolean;
  onFinish: () => void;
  /** Where to send the screen once the night it is about has been deleted. */
  onDeleted: () => void;
}

/**
 * Split out so every derivation below runs on a `SessionState` that exists,
 * without threading a null through twenty expressions or gating hooks on it.
 *
 * Exported for the tests, which render it against a fixed read model: the whole
 * of this screen is what it concludes from one, and the fetch above is not part
 * of that.
 */
export function ResultsView(props: ViewProps): ReactNode {
  const { state, error, channel, busy, failure, justFinished, serverMissing, isGuest } = props;
  const participants = state.participants;
  const total = participants.length;

  /** Which seat's polygon is lifted out of the stack. Null is everybody at once. */
  const [focusSeat, setFocusSeat] = useState<string | null>(null);
  // Memoised because focusing a seat re-renders this whole screen, and the
  // polygons do not depend on which one is lit.
  const radar = useMemo(() => seatRadar(state), [state]);
  const hasShapes = radarHasShapes(radar.series);

  const headline = headlineDistribution(state);
  const headlineProfile = aspectProfile(ratingCells(state.headline), HEADLINE_STOPS, "EVALUATIVE");
  const headlineStep: Step = {
    kind: "headline",
    key: "headline",
    title: "Desire to play again",
    view: state.headline,
  };
  const headlineCells = cellsOf(headlineStep, participants);

  // Which questions hang under which topic. Wanted by the overall roster below
  // and by the follow-up blocks at the bottom, and it is the same grouping — a
  // second one built lower down would be a second answer to the same question.
  const followUpsOf = useMemo(() => followUpsByAspect(state), [state]);

  // Memoised for the reason the topic slices below are: one seat's overall is
  // worked out from every topic and every follow-up they answered, so this walks
  // the whole night per seat. Lighting up a radar seat must not redo it.
  const overallRoster = useMemo(() => overallCells(state, followUpsOf), [state, followUpsOf]);
  // The same array both halves of the block read, through the helper the rail
  // and the roster already share so a row the roster is hiding cannot reach the
  // aggregate. Mapping the cells by hand here worked only by accident: it
  // ignored `visible` entirely, and this screen is built around a night where
  // some seats are still redacted.
  const overallProfile = useMemo(
    () => aspectProfile(visibleRatingCells(overallRoster), OVERALL_STOPS, "EVALUATIVE"),
    [overallRoster],
  );

  // Only revealed aspects are aggregated. A blind one would aggregate to this
  // device's own row and call it the group.
  const revealed = new Map<string, AspectView>();
  const blind: AspectView[] = [];
  for (const view of state.aspects) {
    if (view.revealed) revealed.set(view.aspect.id, view);
    else blind.push(view);
  }

  // Memoised for the same reason the radar above it is, and it now costs more
  // than the radar: a topic's cell is worked out from its follow-ups per seat, so
  // this walks every question in the night. Lighting up one seat must not redo
  // that — it does not depend on which seat is lit.
  const slices = useMemo(
    () => aspectSlices(state).filter((slice) => revealed.has(slice.id)),
    [state],
  );
  const ranked = disagreement(slices);
  const rankedIds = new Set(ranked.map((row) => row.id));
  // Revealed, but nobody left a value — everyone skipped it or called it N/A.
  // `disagreement` drops these rather than ranking silence as agreement, so
  // they are listed on their own instead of vanishing.
  const silent = [...revealed.values()].filter((view) => !rankedIds.has(view.aspect.id));

  const top = ranked[0] ?? null;
  const rest = ranked.slice(1);

  // Follow-ups are listed under their own heading rather than ranked in with
  // the aspects: "Turn pressure" floating above Tension is a finding without
  // its subject. Same reveal rule — a blind one aggregates to this device's own
  // row and is held back with the blind aspects instead.
  const followUps = buildSteps(state).flatMap((step) =>
    step.kind === "subquestion" ? [step] : [],
  );
  const followUpsRevealed = followUps.filter((step) => step.view.revealed);
  const followUpsBlind = followUps.filter((step) => !step.view.revealed);

  // The live read model is the gate, always. The server's own refusal list is
  // kept only to explain the button press, and is intersected with the live one
  // so it disappears by itself the moment the last person answers.
  const gate: MissingSeat[] = missingHeadline(state).map((p) => ({
    participant_id: p.id,
    name: p.name_snapshot,
  }));
  const gateIds = new Set(gate.map((seat) => seat.participant_id));
  const refused = (serverMissing ?? []).filter((seat) => gateIds.has(seat.participant_id));
  const completed = state.session.status === "COMPLETED" || justFinished;
  const badge = channelBadge(completed);

  // The game is what these results are *about*, so it is the heading and the
  // word "Results" goes down beside the date. A tester finished a night, opened
  // this screen and could not tell which game it was — which is close to useless
  // for the one screen people deliberately come back to weeks later.
  //
  // The fallback is for a payload older than the field rather than for a missing
  // game: the server already answers "This game" for a game row it cannot find,
  // but `/api/*` is served from the service worker's cache when the network is
  // gone, and an offline reread of a night is exactly what this screen is for.
  // An empty `h1` would be worse than the server's own plain answer.
  const title = state.game_title || "This game";

  const blockFor = (row: Disagreement, lead: string | undefined, edge: string): ReactNode => {
    const view = revealed.get(row.id);
    if (!view) return null;
    return (
      <Block
        key={row.id}
        title={row.name}
        lead={lead}
        stops={view.aspect.stops_snapshot}
        dist={row.profile.distribution}
        modalLabel={row.profile.modalLabel}
        na={row.profile.na}
        // Not `cellsOf`: `row.profile` came from `aspectSlices`, which works a
        // topic out from its follow-ups for anyone who answered those instead.
        // Read the stored rows here and the rail says five answers while the
        // roster under it names three — the same block disagreeing with itself.
        cells={topicCellsOf(view, followUpsOf.get(view.aspect.id) ?? [], participants)}
        total={total}
        // Decision #25: a descriptive axis is never averaged into anything,
        // including a faint line on its own rail.
        showMean={row.kind === "EVALUATIVE"}
        edge={edge}
      />
    );
  };

  return (
    <div className="pb-screen">
      {/* The box is the fastest way to recognise which night this was — faster
          than reading the title, which is the whole argument for art on the one
          screen people reopen weeks later. */}
      <div className="pb-gamehead">
        <BoxArt
          game={{ title, thumb_url: state.game_thumb_url, image_url: state.game_image_url }}
          size={56}
          eager
        />
        <div className="pb-screen__head pb-gamehead__head">
          <h1 className="pb-screen__title">{title}</h1>
          <span className="pb-screen__sub">Results · {playedOn(state.session.played_at)}</span>
        </div>
      </div>

      {channel && (
        <div className="pb-notice" data-tone={badge.tone}>
          <span className="pb-notice__strong">{badge.label}</span>
          <span>{channel}</span>
        </div>
      )}
      {error && (
        <div className="pb-notice" data-tone="alert">
          <span className="pb-notice__strong">Stale</span>
          <span>{error}</span>
        </div>
      )}

      <p className="pb-hint">{verdictLine(headline, state.headline.revealed, top)}</p>
      {state.session.verdict && (
        <p className="pb-hint">
          <em>&ldquo;{state.session.verdict}&rdquo;</em>
        </p>
      )}

      {/* The shape of the night, before any of the detail. Drawn as soon as the
          screen loads — empty rings and named spokes — rather than appearing
          later and shoving the page down mid-read. */}
      {radar.axes.length >= RADAR_MIN_AXES && (
        <section className="pb-section">
          <div className="pb-label">Everyone, every category</div>
          <Radar
            axes={radar.axes}
            series={radar.series}
            focusId={focusSeat}
            onFocus={setFocusSeat}
            caption={
              !hasShapes
                ? "Shapes appear category by category, as each one reveals."
                : "One shape per person, in their own colour. Tap a name to lift it out of the stack. Further out is not better — most of these categories describe the game rather than judge it, so the size of a shape is not a score."
            }
          />
        </section>
      )}

      {/* The mandatory number. Voted directly, never assembled from anything
          above or below it on this screen. */}
      {state.headline.revealed ? (
        <Block
          title="Desire to play again"
          lead="Everyone typed this one in themselves. Nothing on this screen feeds it."
          stops={HEADLINE_STOPS}
          dist={headline}
          modalLabel={headlineProfile.modalLabel}
          na={0}
          cells={headlineCells}
          total={total}
          showMean
          numeric
          edge="spice"
        />
      ) : (
        <section className="pb-tile pb-tile__stack" data-edge="ochre">
          <h2 className="pb-tile__title">Desire to play again</h2>
          <p className="pb-hint">
            Held back until everyone has answered — otherwise the first number out
            anchors the rest. The night overall is held back with it, because it is
            worked out from the same evening.
          </p>
          <div className="pb-roster">
            {gate.map((seat) => (
              <div className="pb-seat" key={seat.participant_id}>
                <span
                  className="pb-swatch"
                  style={{
                    background: swatchOf(participants, seat.participant_id),
                    outline: "1px solid var(--pb-rule-strong)",
                  }}
                />
                <span className="pb-seat__name">{seat.name}</span>
                <span className="pb-seat__state" data-state="waiting">
                  waiting
                </span>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* The night's overall, beside the headline rather than instead of it.
          Two different questions: one is whether anybody wants a rematch, the
          other is how the evening went. They are allowed to disagree, and a
          group that had a fine time at a game they will never set up again is
          exactly the case the pair exists to record.

          Nobody has to answer it. Each seat's figure is the mean of the topics
          that seat rated — skipped ones are not zeroes, they are absent — unless
          they armed the stars and put their own number on it. Nothing is stored
          for the derived case, so this block cannot be built from the override
          table and is worked out here every time.

          Held to the headline's reveal, which is the whole table's gate. Before
          it, every row but this device's own is redacted away and the block
          would print one person's evening as the group's. */}
      {state.overall.revealed && (
        <Block
          title="The night overall"
          lead="How the evening went, worked out from each person's topics unless they set it themselves."
          stops={OVERALL_STOPS}
          dist={overallProfile.distribution}
          modalLabel={overallProfile.modalLabel}
          na={0}
          cells={overallRoster}
          total={total}
          showMean
          numeric
          edge="spice"
        />
      )}

      {top && (
        <section className="pb-section">
          <div className="pb-label">Where you disagreed most</div>
          {blockFor(
            top,
            top.profile.distribution.split
              ? "The table was split on this. That is a finding, not a mistake — two of you watched different games."
              : top.profile.distribution.spread === 0
                ? "Nothing split the table tonight. Everyone landed on the same stop here."
                : "This is the widest the table spread tonight.",
            top.profile.distribution.split ? "ochre" : "slate",
          )}
        </section>
      )}

      {rest.length > 0 && (
        <section className="pb-section">
          <div className="pb-label">The rest, widest spread first</div>
          {rest.map((row) =>
            blockFor(row, undefined, row.profile.distribution.split ? "ochre" : "muted"),
          )}
        </section>
      )}

      {followUpsRevealed.length > 0 && (
        <section className="pb-section">
          <div className="pb-label">Follow-ups</div>
          {followUpsRevealed.map((step) => {
            const stops = step.view.subquestion.stops_snapshot;
            // A follow-up has no kind of its own and takes its aspect's, so one
            // under a descriptive axis is not averaged either — decision #25.
            const profile = aspectProfile(ratingCells(step.view), stops, step.parentKind);
            return (
              <Block
                key={step.key}
                title={step.title}
                lead={`Under ${step.parentTitle}.`}
                stops={stops}
                dist={profile.distribution}
                modalLabel={profile.modalLabel}
                na={profile.na}
                cells={cellsOf(step, participants)}
                total={total}
                showMean={step.parentKind === "EVALUATIVE"}
                edge={profile.distribution.split ? "ochre" : "muted"}
              />
            );
          })}
        </section>
      )}

      {silent.length > 0 && (
        <section className="pb-section">
          <div className="pb-label">Nobody rated these</div>
          <div className="pb-roster">
            {silent.map((view) => {
              const profile = aspectProfile(
                ratingCells(view),
                view.aspect.stops_snapshot,
                view.aspect.kind_snapshot,
              );
              return (
                <div className="pb-seat" key={view.aspect.id}>
                  <span className="pb-seat__name">{view.aspect.name_snapshot}</span>
                  <span className="pb-seat__state">
                    n = 0{profile.na > 0 ? ` · ${profile.na} n/a` : ""}
                  </span>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {(blind.length > 0 || followUpsBlind.length > 0) && (
        <section className="pb-section">
          <div className="pb-label">Still blind</div>
          <p className="pb-hint">
            These stay hidden until everyone has answered them. Aspects and their follow-ups
            are optional, so they never hold up finishing.
          </p>
          <div className="pb-roster">
            {blind.map((view) => {
              const waiting = total - view.ratings.length;
              return (
                <div className="pb-seat" key={view.aspect.id}>
                  <span className="pb-seat__name">{view.aspect.name_snapshot}</span>
                  <span className="pb-seat__state" data-state="waiting">
                    {waiting > 0 ? `${waiting} to go` : "waiting"}
                  </span>
                </div>
              );
            })}
            {followUpsBlind.map((step) => {
              const waiting = total - step.view.ratings.length;
              return (
                <div className="pb-seat" key={step.key}>
                  <span className="pb-seat__name">
                    {step.parentTitle} · {step.title}
                  </span>
                  <span className="pb-seat__state" data-state="waiting">
                    {waiting > 0 ? `${waiting} to go` : "waiting"}
                  </span>
                </div>
              );
            })}
          </div>
        </section>
      )}

      <p className="pb-hint">
        Each dot is one person, in their own colour. Different aspects have different
        counts because any of them can be skipped, and &ldquo;doesn&rsquo;t apply&rdquo;
        is a real answer. A split is {formatStars(SPLIT_THRESHOLD)} stars or more
        between the furthest two of you.
      </p>

      <hr className="pb-rule" />

      {completed ? (
        <div className="pb-notice" data-tone="ok">
          <span className="pb-notice__strong">Done</span>
          <span>This session is finished and saved.</span>
        </div>
      ) : (
        refused.length > 0 && (
          <div className="pb-notice" data-tone="wait">
            <span className="pb-notice__strong">Not yet</span>
            <span>
              {refused.length === 1
                ? `${refused[0]?.name ?? "Someone"} still owes the one number that matters.`
                : `${refused.length} of you still owe the one number that matters.`}
            </span>
          </div>
        )
      )}

      {failure && (
        <div className="pb-notice" data-tone="alert">
          <span className="pb-notice__strong">Error</span>
          <span>{failure}</span>
        </div>
      )}

      <div className="pb-actions">
        <button
          type="button"
          className="pb-btn"
          data-variant="quiet"
          onClick={() => navigate(completed ? "/" : `/session/${state.session.id}`)}
        >
          {completed ? "Back to the table" : "Back to rating"}
        </button>
        {!completed && !isGuest && (
          <button
            type="button"
            className="pb-btn"
            data-variant="primary"
            disabled={busy}
            // Never disabled on the client's own view of the gate: this device
            // may be holding an unsynced headline, so the server decides.
            onClick={props.onFinish}
          >
            {busy ? "Finishing…" : "Finish session"}
          </button>
        )}
      </div>

      {!completed && isGuest && (
        <p className="pb-hint">The host finishes the session on their device.</p>
      )}

      {/*
        Host only, for the same reason "Finish session" is: a guest device holds
        a seat grant, and the delete endpoint refuses grants outright. Last on
        the screen and below the rule, so the way to throw the night away is
        never in reach of somebody scrolling the board.
      */}
      {!isGuest && (
        <DeleteNight sessionId={state.session.id} onDeleted={props.onDeleted} />
      )}
    </div>
  );
}

function swatchOf(participants: readonly SessionParticipant[], id: string): string {
  const found = participants.find((p) => p.id === id);
  return found ? seatColour(found) : "var(--pb-ink-faint)";
}
